/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/definition/rib 层；
 * 2. 负责定义筋特征参数真相对象；
 * 3. 筋定义对象只承载结构化参数，不执行任何几何逻辑。
 */

import { FeatureDefinition, type FeatureDefinitionSnapshot } from '../FeatureDefinition';

/**
 * 筋特征定义快照。
 */
export interface RibFeatureDefinitionSnapshot extends FeatureDefinitionSnapshot {
    /** 当前特征种类。 */
    featureKind: 'rib';
    /** 原始命令参数。 */
    commandParams: RibParams;
}

/**
 * 筋特征定义投影。
 */
export interface RibFeatureDefinitionProjection {
    /** 上游实体对象 ID。 */
    upstreamBodyObjectId: string | null;
    /** 结果对象 ID。 */
    resultObjectId: string | null;
    /** 筋定义。 */
    definition: RibFeatureDefinition;
    /** 特征名称。 */
    name: string | null;
}

/**
 * 筋特征定义对象。
 */
export class RibFeatureDefinitionModel extends FeatureDefinition<RibFeatureDefinitionSnapshot> {
    /**
     * 基于命令参数创建筋定义对象。
     *
     * @param input 输入参数。
     * @returns 筋定义对象。
     */
    public static fromParams(input: {
        commandParams: RibParams;
        version?: number;
    }): RibFeatureDefinitionModel {
        return new RibFeatureDefinitionModel({
            featureKind: 'rib',
            version: input.version ?? 1,
            commandParams: structuredClone(input.commandParams),
        });
    }

    /**
     * @param snapshot 定义快照。
     */
    public constructor(snapshot: RibFeatureDefinitionSnapshot) {
        super(snapshot);
    }

    /**
     * 获取原始命令参数。
     *
     * @returns 命令参数快照。
     */
    public getCommandParams(): RibParams {
        return this.toSnapshot().commandParams;
    }

    /**
     * 获取筋领域定义。
     *
     * @returns 筋定义快照。
     */
    public getRibDefinition(): RibFeatureDefinition {
        return structuredClone(this.toSnapshot().commandParams.definition);
    }

    /**
     * 获取上游实体对象 ID。
     *
     * @returns 上游实体对象 ID。
     */
    public getUpstreamBodyObjectId(): string | null {
        const value = this.toSnapshot().commandParams.upstreamBodyObjectId;
        return typeof value === 'string' ? value : null;
    }

    /**
     * 生成面向 UI 的投影快照。
     *
     * @returns 投影快照。
     */
    public toProjection(): RibFeatureDefinitionProjection {
        const commandParams = this.toSnapshot().commandParams;
        return {
            upstreamBodyObjectId: typeof commandParams.upstreamBodyObjectId === 'string'
                ? commandParams.upstreamBodyObjectId
                : null,
            resultObjectId: typeof commandParams.resultObjectId === 'string'
                ? commandParams.resultObjectId
                : null,
            definition: structuredClone(commandParams.definition),
            name: typeof commandParams.name === 'string' ? commandParams.name : null,
        };
    }

    /**
     * 基于新命令参数生成下一份定义对象。
     *
     * @param commandParams 新命令参数。
     * @returns 新定义对象。
     */
    public withCommandParams(commandParams: RibParams): RibFeatureDefinitionModel {
        return RibFeatureDefinitionModel.fromParams({
            commandParams,
            version: this.getVersion(),
        });
    }
}
